import { Component, type ErrorInfo, type ReactNode } from "react";

import { useT } from "../../i18n";

interface Props {
  children: ReactNode;
}
interface State {
  hasError: boolean;
}

/**
 * The fallback is its own function component because an error boundary has to
 * be a class — `getDerivedStateFromError` has no hook equivalent — and a class
 * cannot call `useT()`. Only the message needs the dictionary, so only the
 * message moves.
 */
function ErrorFallback() {
  const t = useT();
  return (
    <div className="flex h-full items-center justify-center bg-surface-base p-6">
      <div className="max-w-sm text-center">
        <h1 className="text-xl font-semibold text-text-primary">{t.states.errorTitle}</h1>
        <p className="mt-2 text-sm text-text-secondary">{t.states.errorBody}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-text-onAccent transition-colors hover:bg-accent-hover"
        >
          {t.states.errorRetry}
        </button>
      </div>
    </div>
  );
}

/**
 * Catches render-time crashes anywhere below it and shows a plain, actionable
 * message instead of a blank screen (brief §13). Errors are logged for
 * diagnosis; the only recovery offered is a reload, since state may be corrupt.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[app] render error", error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return <ErrorFallback />;
  }
}
